import {doc, getDoc, updateDoc} from 'firebase/firestore';
import {useState} from 'react';
import {env} from '../../constants';
import {firebaseAuth, firebaseFirestore} from '../../setup/firebase';
import {extractSheetIdFromUrl} from './extractSheetIdFromUrl';
import {generationJobProjectInputSchema, type GenerationJobProject, type GenerationJobProjectInput} from './schema';
import {resolveOutputFolderId} from './resolveOutputFolderId';

export interface UseUpdateGenerationJobProjectOptions {
  onCompleted?: (data: GenerationJobProject) => void;
  onError?: (error: Error) => void;
}

export default function useUpdateGenerationJobProject(options?: UseUpdateGenerationJobProjectOptions) {
  const [loading, setLoading] = useState(false);

  const updateProject = async (id: string, input: GenerationJobProjectInput) => {
    const user = firebaseAuth.currentUser;
    if (!user) {
      const err = new Error('Not authenticated');
      options?.onError?.(err);
      return;
    }
    setLoading(true);
    try {
      const parsed = generationJobProjectInputSchema.parse(input);
      const docRef = doc(firebaseFirestore, 'user', user.uid, env.collection.generationJobProject, id);
      const snap = await getDoc(docRef);
      if (!snap.exists()) {
        throw new Error('Project not found');
      }
      const sheetId = extractSheetIdFromUrl(parsed.sheetUrlOrId);
      const outputFolderId = resolveOutputFolderId(parsed.outputFolderUrlOrId);
      const sourceImageFileId = parsed.sourceImageFileId?.trim();
      const sourceImageUrl = parsed.sourceImageUrl?.trim();
      const data: Record<string, string | number | boolean | null> = {
        gdriveTokenId: parsed.gdriveTokenId,
        sheetId,
        outputFolderId,
      };
      if (parsed.name !== undefined) data.name = parsed.name || null;
      if (sourceImageFileId !== undefined) data.sourceImageFileId = sourceImageFileId || null;
      if (sourceImageUrl !== undefined) data.sourceImageUrl = sourceImageUrl || null;
      if (parsed.systemPrompt !== undefined) data.systemPrompt = parsed.systemPrompt?.trim() || null;
      if (parsed.edit !== undefined) data.edit = parsed.edit;
      if (parsed.outputQuality !== undefined) data.outputQuality = parsed.outputQuality;
      if (parsed.outputShape !== undefined) data.outputShape = parsed.outputShape;
      await updateDoc(docRef, data);
      const updated = (await getDoc(docRef)).data();
      const result: GenerationJobProject = {
        id: snap.id,
        name: typeof updated?.name === 'string' ? updated.name : undefined,
        gdriveTokenId: typeof updated?.gdriveTokenId === 'string' ? updated.gdriveTokenId : '',
        sourceImageFileId: typeof updated?.sourceImageFileId === 'string' ? updated.sourceImageFileId : undefined,
        sourceImageUrl: typeof updated?.sourceImageUrl === 'string' ? updated.sourceImageUrl : undefined,
        sheetId: typeof updated?.sheetId === 'string' ? updated.sheetId : '',
        outputFolderId: typeof updated?.outputFolderId === 'string' ? updated.outputFolderId : '',
        creationTimestamp: typeof updated?.creationTimestamp === 'number' ? updated.creationTimestamp : 0,
        systemPrompt: typeof updated?.systemPrompt === 'string' ? updated.systemPrompt : undefined,
        edit: typeof updated?.edit === 'boolean' ? updated.edit : undefined,
        outputQuality: updated?.outputQuality as GenerationJobProject['outputQuality'],
        outputShape: updated?.outputShape as GenerationJobProject['outputShape'],
      };
      options?.onCompleted?.(result);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      options?.onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return [updateProject, {loading}] as const;
}
